import React from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditAddressForm from "./EditAddressForm";

const PRIMARY = "#047857";

function formatDistrictLabel(district) {
  if (district === null || district === undefined || district === "" || district === "N/A") {
    return null;
  }
  const digits = String(district).replace(/^[^\d]*/, "");
  if (!digits) return null;
  // At-large states and DC are stored as district 0.
  return Number(digits) === 0 ? "At-Large District" : `District ${digits}`;
}

function repNames(calculatedReps) {
  if (!calculatedReps) return [];
  return [
    calculatedReps.representative?.name,
    calculatedReps.senator_one?.name,
    calculatedReps.senator_two?.name,
  ].filter((name) => name && name !== "NOT FOUND");
}

export default function ContactLocationSection({
  formData,
  handleChange,
  onAddressSaved,
  repsRecalcStatus = "idle",
  repsRecalcErrorMessage = null,
  calculatedReps = null,
}) {
  const names = repNames(calculatedReps);
  const districtText = formatDistrictLabel(calculatedReps?.district);

  return (
    <Box id="contact" sx={{ mb: 4, scrollMarginTop: "88px" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#111", mb: 2 }}>
        Contact
      </Typography>
      <Stack spacing={2.5}>
        <TextField
          fullWidth
          label="Email Address"
          value={formData.email || ""}
          disabled
          variant="outlined"
          size="small"
          helperText="Email cannot be changed directly."
        />

        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phone_number || ""}
          onChange={handleChange("phone_number")}
          variant="outlined"
          size="small"
        />
      </Stack>

      <Box id="location" sx={{ scrollMarginTop: "88px", mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#111", mb: 2 }}>
          Location
        </Typography>
        <Stack spacing={2.5}>
          <EditAddressForm
            initialAddress={{
              street_1: formData.street_1,
              city: formData.city,
              state: formData.state,
              zipcode: formData.zipcode,
            }}
            onAddressSaved={onAddressSaved}
          />

          <Box aria-live="polite">
            {repsRecalcStatus === "updating" && (
              <Alert
                icon={<CircularProgress size={16} sx={{ color: PRIMARY }} />}
                severity="info"
                sx={{
                  bgcolor: `${PRIMARY}0d`,
                  color: "#0f172a",
                  border: `1px solid ${PRIMARY}33`,
                  py: 0.5,
                  "& .MuiAlert-icon": { color: PRIMARY, alignItems: "center" },
                }}
              >
                Calculating your representatives...
              </Alert>
            )}
            {repsRecalcStatus === "done" && (
              <Alert
                icon={<CheckCircleOutlineIcon fontSize="small" sx={{ color: PRIMARY }} />}
                severity="success"
                sx={{
                  bgcolor: `${PRIMARY}0d`,
                  color: "#0f172a",
                  border: `1px solid ${PRIMARY}33`,
                  py: 0.75,
                }}
              >
                {names.length > 0 ? (
                  <>
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: 14 }}>
                      Representatives updated
                      {districtText ? ` — ${districtText}` : ""}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "#0f172a", mt: 0.25 }}>
                      {names.join(", ")}
                    </Typography>
                  </>
                ) : (
                  "Representatives updated."
                )}
              </Alert>
            )}
            {repsRecalcStatus === "error" && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {repsRecalcErrorMessage ||
                  "Could not update your representatives automatically. Try saving the address again."}
              </Alert>
            )}
          </Box>

          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.is_state_visible && formData.is_district_visible)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    handleChange("is_state_visible")({ target: { value: checked } });
                    handleChange("is_district_visible")({ target: { value: checked } });
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Show state and district on public profile
                </Typography>
              }
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
